import { useState, useCallback, useEffect } from 'react';
import { Card, CardHeader, Button, Badge, Pagination, LoadingSkeleton, Input, Modal } from '@nexora/design-system';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import { useBankAccounts } from './useBankAccounts';
import { useCustomerLookup } from '../../shared/lookups/useParties';
import {
  daysOverdue,
  useReceivables,
  useReceivablesAging,
  useCreateReceivable,
  useUpdateReceivable,
  useSettleReceivable,
  useRenegotiateReceivable,
  useDeleteReceivable,
  type Receivable,
  type ReceivableInput,
  type SettleInput,
  type RenegotiateInput,
} from './useReceivables';

/* Coluna de dinheiro alinha a direita, em mono tabular: com valores de
   larguras diferentes a esquerda, a casa decimal nao alinha e a leitura de
   coluna exige releitura linha a linha. E o que o DESIGN.md prescreve e o
   que a tela de referencia faz. */
const NUMERIC_COLUMNS = new Set(['Valor']);
const MONEY_CELL = {
  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
  fontVariantNumeric: 'tabular-nums' as const,
  textAlign: 'right' as const,
};

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'OPEN', label: 'Em aberto' },
  { value: 'PARTIALLY_SETTLED', label: 'Parc. recebido' },
  { value: 'OVERDUE', label: 'Vencido' },
  { value: 'SETTLED', label: 'Recebido' },
  { value: 'RENEGOTIATED', label: 'Renegociado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

function statusBadge(status: Receivable['status']) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info'; label: string }> = {
    OPEN: { variant: 'info', label: 'Em aberto' },
    PARTIALLY_SETTLED: { variant: 'info', label: 'Parc. recebido' },
    SETTLED: { variant: 'success', label: 'Recebido' },
    OVERDUE: { variant: 'error', label: 'Vencido' },
    RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
    CANCELLED: { variant: 'error', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'info', label: status };
}

function isActionable(status: Receivable['status']): boolean {
  return status !== 'SETTLED' && status !== 'CANCELLED' && status !== 'RENEGOTIATED';
}

export function ReceivablesPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [modalOpen, setModalOpen] = useState(false);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [renegModalOpen, setRenegModalOpen] = useState(false);
  const [selectedReceivable, setSelectedReceivable] = useState<Receivable | undefined>(undefined);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');

  const { data, isLoading, error } = useReceivables({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    page,
    limit: 20,
  });

  const { data: aging } = useReceivablesAging();

  const createMutation = useCreateReceivable();
  const updateMutation = useUpdateReceivable();
  const settleMutation = useSettleReceivable();
  const renegMutation = useRenegotiateReceivable();
  const deleteMutation = useDeleteReceivable();

  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  const showToast = useCallback((message: string, variant: 'success' | 'error') => {
    setToastMessage(message);
    setToastVariant(variant);
  }, []);

  const handleCreate = useCallback(
    async (input: ReceivableInput) => {
      try {
        await createMutation.mutateAsync(input);
        showToast('Conta a receber criada com sucesso.', 'success');
        setModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao criar.', 'error');
      }
    },
    [createMutation, showToast],
  );

  const handleUpdate = useCallback(
    async (input: ReceivableInput) => {
      if (!selectedReceivable) return;
      try {
        await updateMutation.mutateAsync({ id: selectedReceivable.id, ...input });
        showToast('Conta atualizada com sucesso.', 'success');
        setModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao atualizar.', 'error');
      }
    },
    [selectedReceivable, updateMutation, showToast],
  );

  const handleSettle = useCallback(
    async (input: SettleInput) => {
      if (!selectedReceivable) return;
      try {
        await settleMutation.mutateAsync({ id: selectedReceivable.id, ...input });
        showToast('Recebimento registrado com sucesso.', 'success');
        setSettleModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao registrar recebimento.', 'error');
      }
    },
    [selectedReceivable, settleMutation, showToast],
  );

  const handleRenegotiate = useCallback(
    async (input: RenegotiateInput) => {
      if (!selectedReceivable) return;
      try {
        await renegMutation.mutateAsync({ id: selectedReceivable.id, ...input });
        showToast('Renegociação registrada com sucesso.', 'success');
        setRenegModalOpen(false);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao renegociar.', 'error');
      }
    },
    [selectedReceivable, renegMutation, showToast],
  );

  const handleEdit = useCallback((receivable: Receivable) => {
    setSelectedReceivable(receivable);
    setModalOpen(true);
  }, []);

  const handleNew = useCallback(() => {
    setSelectedReceivable(undefined);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await deleteMutation.mutateAsync(id);
        showToast('Conta excluída com sucesso.', 'success');
        setDeleteConfirmId(null);
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Erro ao excluir.', 'error');
        setDeleteConfirmId(null);
      }
    },
    [deleteMutation, showToast],
  );

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const totalPages = data?.totalPages ?? 1;
  const receivables = data?.data ?? [];

  return (
    /* ───────────────────────────────────────────────────────────────────────
       Portado de base-html, slug `erpone-gest-o-de-t-tulos-contas-a-receber`.
       Espelha a estrutura do Contas a Pagar, como no protótipo.

       O aging vem de GET /finance/receivables/aging — endpoint que estava
       SOMBREADO pela rota `:id` declarada antes e devolvia 404. Corrigido no
       backend todo33.
       ─────────────────────────────────────────────────────────────────────── */
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Gestão de Títulos
            {data && (
              <span className="font-body-md text-body-md text-on-surface-variant font-normal ml-sm">
                ({data.total})
              </span>
            )}
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Contas a receber, vencimentos e cobrança.
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <button
            type="button"
            onClick={handleNew}
            className="bg-primary text-on-primary hover:bg-primary-container px-md py-sm rounded font-label-md text-label-md transition-colors shadow-sm flex items-center gap-xs"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Nova Conta
          </button>
        </div>
      </div>

      {/* Aging — grid do prototipo, valores em mono tabular */}
      {aging && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-md mb-lg">
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">A receber</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">event_available</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-success-green m-0 tabular-nums">
              {formatCurrency(aging.current)}
            </p>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">1-30 dias</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">schedule</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-warning-amber m-0 tabular-nums">
              {formatCurrency(aging.days30)}
            </p>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">31-60 dias</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">running_with_errors</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-warning-amber m-0 tabular-nums">
              {formatCurrency(aging.days60)}
            </p>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">61-90 dias</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">warning</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-error m-0 tabular-nums">
              {formatCurrency(aging.days90)}
            </p>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">90+ dias</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">report</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-error m-0 tabular-nums">
              {formatCurrency(aging.days120plus)}
            </p>
          </div>
          <div className="bg-surface-container-lowest rounded-xl border border-primary/30 bg-surface-container-low p-md shadow-sm">
            <div className="flex items-center justify-between mb-xs">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase">Total</span>
              <span className="material-symbols-outlined text-[16px] text-on-surface-variant">functions</span>
            </div>
            <p className="font-headline-sm text-headline-sm text-primary m-0 tabular-nums">
              {formatCurrency(aging.total)}
            </p>
          </div>
        </div>
      )}

      {/* Toolbar de filtro */}
      <div className="bg-surface-container-lowest rounded-xl border border-outline-variant shadow-sm p-md mb-lg flex flex-wrap items-end gap-md">
        <div className="flex flex-col">
          <label
            htmlFor="receivables-status"
            className="font-label-sm text-label-sm text-on-surface-variant uppercase mb-xs"
          >
            Status
          </label>
          <select
            id="receivables-status"
            value={statusFilter}
            onChange={handleFilterChange}
            className="bg-surface-container-low border border-outline-variant font-body-sm text-body-sm rounded py-2 px-sm focus:ring-1 focus:ring-primary focus:border-primary cursor-pointer min-w-[220px]"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'título' : 'títulos'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Títulos a receber</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton /><span style={{ fontSize: '0.875rem' }}>Carregando contas a receber...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar contas a receber</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(error as Error).message}</p>
          </div>
        )}
        {!isLoading && !error && receivables.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhuma conta a receber encontrada.</p>
            <Button variant="outline" size="sm" onClick={handleNew}>Criar primeira conta</Button>
          </div>
        )}
        {!isLoading && !error && receivables.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Cliente', 'Descrição', 'Valor', 'Vencimento', 'Dias', 'Status', 'Ações'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: NUMERIC_COLUMNS.has(h) ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {receivables.map((r) => (
                  <tr key={r.id} style={{ transition: 'background 0.15s ease' }} className="receivable-row">
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>{r.customerName}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', color: '#43474e' }}>
                      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: '12px' }}>
                        {r.documentNumber}
                      </span>
                      {r.category && (
                        <span style={{ display: 'block', fontSize: '12px', color: '#74777f' }}>{r.category}</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600, ...MONEY_CELL }}>{formatCurrency(r.openBalance)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>{formatDate(r.dueDate)}</td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                      {/* Derivado de `dueDate`: o backend nao manda o numero. */}
                      {daysOverdue(r) > 0 ? (
                        <Badge variant="error">{daysOverdue(r)}d</Badge>
                      ) : (
                        <span style={{ color: '#74777f' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}><Badge variant={statusBadge(r.status).variant} size="sm" dot>{statusBadge(r.status).label}</Badge></td>
                    <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                        {isActionable(r.status) && (<Button variant="ghost" size="sm" onClick={() => { setSelectedReceivable(r); setSettleModalOpen(true); }}>Receber</Button>)}
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(r)}>Editar</Button>
                        {isActionable(r.status) && (<Button variant="ghost" size="sm" onClick={() => { setSelectedReceivable(r); setRenegModalOpen(true); }}>Renegociar</Button>)}
                        {deleteConfirmId === r.id ? (
                          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#ba1a1a' }}>Excluir?</span>
                            <Button variant="danger" size="sm" loading={deleteMutation.isPending} onClick={() => handleDelete(r.id)}>Sim</Button>
                            <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(null)}>Não</Button>
                          </div>
                        ) : (<Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(r.id)} style={{ color: '#ba1a1a' }}>Excluir</Button>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && totalPages > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={page} totalPages={totalPages} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      {/* Toast */}
      {toastMessage && (
        <div role="alert" style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', padding: '0.75rem 1rem', borderRadius: '8px', background: toastVariant === 'success' ? '#d3f5d6' : '#ffdad6', border: `1px solid ${toastVariant === 'success' ? '#b6ebbb' : '#ffb4ab'}`, color: toastVariant === 'success' ? '#2e7d32' : '#ba1a1a', fontSize: '0.875rem', fontWeight: 500, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10000, maxWidth: '400px' }}>
          {toastMessage}
          <button onClick={() => setToastMessage(null)} aria-label="Fechar" style={{ marginLeft: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: '1rem', padding: 0, lineHeight: 1 }}>&times;</button>
        </div>
      )}

      <ReceivableFormModal isOpen={modalOpen} onClose={() => { setModalOpen(false); setSelectedReceivable(undefined); }} receivable={selectedReceivable} onSubmit={selectedReceivable ? handleUpdate : handleCreate} />
      <SettleReceivableModal isOpen={settleModalOpen} onClose={() => { setSettleModalOpen(false); setSelectedReceivable(undefined); }} receivable={selectedReceivable} onSubmit={handleSettle} />
      <RenegotiateReceivableModal isOpen={renegModalOpen} onClose={() => { setRenegModalOpen(false); setSelectedReceivable(undefined); }} receivable={selectedReceivable} onSubmit={handleRenegotiate} />

      <style>{`
        .fin-form { display: flex; flex-direction: column; gap: 1rem; }
        .fin-form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 1rem; }
        .fin-form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; }
        .fin-field { display: flex; flex-direction: column; gap: 0.25rem; }
        .fin-label { margin: 0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #43474e; }
        .fin-value { margin: 0; font-size: 0.875rem; color: #191c1e; }
        .fin-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #43474e; }
        .fin-context { margin: 0; font-size: 0.875rem; color: #43474e; }
        .fin-select { padding: 0.5rem; background: #f7f9fb; border: 1px solid #c4c6cf; border-radius: 2px; font: inherit; font-size: 0.875rem; color: #191c1e; }
        .fin-select:focus { outline: none; border-color: #002045; box-shadow: 0 0 0 1px #002045; }
        .fin-picked { display: flex; align-items: center; justify-content: space-between; gap: 1rem; border: 1px solid #c4c6cf; border-radius: 4px; padding: 0.5rem 0.75rem; background: #f7f9fb; }
        .fin-lookup { list-style: none; margin: 0.25rem 0 0; padding: 0; border: 1px solid #c4c6cf; border-radius: 4px; background: #fff; max-height: 200px; overflow-y: auto; }
        .fin-lookup li + li { border-top: 1px solid #eceef0; }
        .fin-lookup button { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; width: 100%; padding: 0.5rem 0.75rem; background: none; border: none; text-align: left; cursor: pointer; font: inherit; }
        .fin-lookup button:hover { background: #f2f4f6; }
        .fin-installments { list-style: none; margin: 0; padding: 0.5rem 0.75rem; border: 1px solid #c4c6cf; border-radius: 4px; background: #f7f9fb; }
        .fin-installments li { display: grid; grid-template-columns: 40px 1fr auto; gap: 0.5rem; font-size: 0.875rem; padding: 0.125rem 0; }
.receivable-row:hover td { background: #eceef0; }.aging-total-card { background: #f2f4f6; }.filter-card { margin-bottom: 1rem; }`}</style>
    </div>
  );
}

/* ── Sub-components ── */

function ReceivableFormModal({ isOpen, onClose, receivable, onSubmit }: {
  isOpen: boolean; onClose: () => void; receivable?: Receivable; onSubmit: (input: ReceivableInput) => Promise<void>;
}) {
  const [customerId, setCustomerId] = useState(receivable?.customerId ?? '');
  const [customerName, setCustomerName] = useState(receivable?.customerName ?? '');
  const [customerSearch, setCustomerSearch] = useState('');
  const [documentNumber, setDocumentNumber] = useState(receivable?.documentNumber ?? '');
  const [documentDate, setDocumentDate] = useState(
    receivable?.documentDate?.split('T')[0] ?? new Date().toISOString().split('T')[0] ?? '',
  );
  const [dueDate, setDueDate] = useState(receivable?.dueDate?.split('T')[0] ?? '');
  const [originalAmount, setOriginalAmount] = useState(receivable?.originalAmount?.toString() ?? '');
  const [category, setCategory] = useState(receivable?.category ?? '');
  const [notes, setNotes] = useState(receivable?.notes ?? '');
  const [submitting, setSubmitting] = useState(false);

  const customers = useCustomerLookup(customerId ? '' : customerSearch);

  const valid = !!customerId && !!customerName && !!documentNumber && !!dueDate && !!originalAmount && !!category;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        customerId,
        customerName,
        documentNumber,
        documentDate,
        dueDate,
        originalAmount: parseFloat(originalAmount),
        category,
        ...(notes ? { notes } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={receivable ? 'Editar conta a receber' : 'Nova conta a receber'}>
      <form onSubmit={handleSubmit} className="fin-form">
        {customerId ? (
          <div className="fin-picked">
            <div>
              <p className="fin-label">Cliente</p>
              <p className="fin-value">{customerName}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                setCustomerId('');
                setCustomerName('');
              }}
            >
              Trocar
            </Button>
          </div>
        ) : (
          <div>
            <Input
              label="Cliente"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              placeholder="Buscar por nome ou documento (mín. 2 letras)"
            />
            {(customers.data?.data.length ?? 0) > 0 && (
              <ul className="fin-lookup">
                {customers.data?.data.map((cus) => (
                  <li key={cus.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setCustomerId(cus.id);
                        setCustomerName(cus.name);
                        setCustomerSearch('');
                      }}
                    >
                      <span>{cus.name}</span>
                      <span className="fin-mono">{cus.documentNumber ?? '—'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <Input
          label="Documento"
          value={documentNumber}
          onChange={(e) => setDocumentNumber(e.target.value)}
          placeholder="NF, boleto ou contrato"
          required
        />
        <div className="fin-form-row">
          <Input label="Emissão" type="date" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} required />
          <Input label="Vencimento" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
        </div>
        <div className="fin-form-row">
          <Input label="Valor (R$)" type="number" step="0.01" value={originalAmount} onChange={(e) => setOriginalAmount(e.target.value)} required />
          <Input label="Categoria" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Ex.: Vendas" required />
        </div>
        <Input label="Observações" value={notes ?? ''} onChange={(e) => setNotes(e.target.value)} />

        <div className="fin-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>
            {receivable ? 'Atualizar' : 'Criar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function SettleReceivableModal({ isOpen, onClose, receivable, onSubmit }: {
  isOpen: boolean; onClose: () => void; receivable?: Receivable; onSubmit: (input: SettleInput) => Promise<void>;
}) {
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0] ?? '');
  const [amount, setAmount] = useState(receivable?.openBalance?.toString() ?? '');
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [accountId, setAccountId] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: accounts } = useBankAccounts();

  useEffect(() => {
    if (!accountId && accounts && accounts.length > 0) setAccountId(accounts[0]?.id ?? '');
  }, [accounts, accountId]);

  useEffect(() => {
    if (receivable) setAmount(receivable.openBalance.toString());
  }, [receivable]);

  const valid = !!amount && !!receivedDate && !!paymentMethod && !!accountId;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({
        amount: parseFloat(amount),
        receivedDate,
        paymentMethod,
        accountId,
        ...(notes ? { notes } : {}),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar recebimento">
      <form onSubmit={handleSubmit} className="fin-form">
        {receivable && (
          <p className="fin-context">
            {receivable.customerName} · {receivable.documentNumber} · em aberto{' '}
            {formatCurrency(receivable.openBalance)}
          </p>
        )}
        <div className="fin-form-row">
          <Input label="Data do recebimento" type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} required />
          <Input label="Valor recebido (R$)" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </div>
        <div className="fin-form-row">
          <div className="fin-field">
            <label htmlFor="recv-method" className="fin-label">Forma de recebimento</label>
            <select id="recv-method" className="fin-select" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="PIX">PIX</option>
              <option value="TED">TED</option>
              <option value="BOLETO">Boleto</option>
              <option value="DINHEIRO">Dinheiro</option>
              <option value="CARTAO">Cartão</option>
            </select>
          </div>
          <div className="fin-field">
            <label htmlFor="recv-account" className="fin-label">Conta</label>
            <select id="recv-account" className="fin-select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {(accounts ?? []).length === 0 && <option value="">Nenhuma conta cadastrada</option>}
              {(accounts ?? []).map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} {acc.bankName ? `· ${acc.bankName}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Input label="Observações" value={notes} onChange={(e) => setNotes(e.target.value)} />

        <div className="fin-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>Registrar recebimento</Button>
        </div>
      </form>
    </Modal>
  );
}

function RenegotiateReceivableModal({ isOpen, onClose, receivable, onSubmit }: {
  isOpen: boolean; onClose: () => void; receivable?: Receivable; onSubmit: (input: RenegotiateInput) => Promise<void>;
}) {
  const [installmentCount, setInstallmentCount] = useState('2');
  const [firstDueDate, setFirstDueDate] = useState('');
  const [totalAmount, setTotalAmount] = useState(receivable?.openBalance?.toString() ?? '');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (receivable) setTotalAmount(receivable.openBalance.toString());
  }, [receivable]);

  const count = Math.max(1, Math.min(60, parseInt(installmentCount, 10) || 1));
  const total = parseFloat(totalAmount) || 0;
  const installments = (() => {
    if (!firstDueDate || total <= 0) return [];
    const base = +(total / count).toFixed(2);
    return Array.from({ length: count }, (_, i) => {
      const due = new Date(firstDueDate);
      due.setMonth(due.getMonth() + i);
      const amount = i === count - 1 ? +(total - base * (count - 1)).toFixed(2) : base;
      return { amount, dueDate: due.toISOString().split('T')[0] ?? firstDueDate };
    });
  })();

  const valid = installments.length > 0 && reason.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSubmit({ installments, reason });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Renegociar recebimento">
      <form onSubmit={handleSubmit} className="fin-form">
        {receivable && (
          <p className="fin-context">
            {receivable.customerName} · em aberto {formatCurrency(receivable.openBalance)}
          </p>
        )}
        <div className="fin-form-row">
          <Input label="Parcelas" type="number" min="1" max="60" value={installmentCount} onChange={(e) => setInstallmentCount(e.target.value)} required />
          <Input label="Primeiro vencimento" type="date" value={firstDueDate} onChange={(e) => setFirstDueDate(e.target.value)} required />
        </div>
        <Input label="Valor total (R$)" type="number" step="0.01" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} required />
        <Input label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Acordo com o cliente" required />

        {installments.length > 0 && (
          <ul className="fin-installments">
            {installments.map((inst, i) => (
              <li key={inst.dueDate + String(i)}>
                <span>{i + 1}ª</span>
                <span>{formatDate(inst.dueDate)}</span>
                <span className="fin-mono">{formatCurrency(inst.amount)}</span>
              </li>
            ))}
          </ul>
        )}

        <div className="fin-form-actions">
          <Button variant="outline" type="button" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!valid}>Confirmar renegociação</Button>
        </div>
      </form>
    </Modal>
  );
}
